import { FC, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { BrandTabsTypes } from 'stores/brands/types';
import { AppLoader } from 'components/Core/AppLoader';
import { BrandBasicForm } from 'components/Forms/BrandBasicForm/BrandBasicForm';
import { BrandsCataloguesTable } from 'components/Tables/Brand/BrandsCataloguesTable/BrandsCataloguesTable';
import BrandCatalogue from 'components/BrandComponents/BrandCatalogue/BrandCatalogue';
import { EntityTitle } from 'components/Core/EntityTitle';
import { brandTabs } from './constants';
import { brandTranslations } from './translations';
import { BrandTransFields } from './types';
import styles from './brand.module.scss';

const Brand: FC = observer(() => {
  const {
    brandsStore: { brandBasicData, isBrand, getBrandBasicData, getBrandCatalogues },
  } = useStore();

  const { t } = useMyTranslations<BrandTransFields>([brandTranslations.name]);

  const { id, catalogueId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [currentTab, setCurrentTab] = useState<BrandTabsTypes>(
    brandTabs.find((tab) => tab === pathname.split('/')[3]) || 'basic'
  );

  const { isLoading, mutate } = useMutation(getBrandCatalogues, {
    mutationKey: 'getBrandCatalogues',
  });

  const { isFetching } = useQuery('getBrandBasicData', () => (id ? getBrandBasicData(id) : null), {
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data && id) {
        if (data.deletedDate && currentTab !== 'basic') {
          navigate(`/brands/${id}/basic`);
          setCurrentTab('basic');
        } else if (currentTab === 'catalogues') {
          mutate(id);
        }
      }
    },
  });

  const title = useMemo(() => {
    if (isBrand) {
      return `${t('title')} "${brandBasicData?.name || id}"`;
    }
    return t('addTitle');
  }, [brandBasicData, id, isBrand]);

  const availableTabs = useMemo(() => {
    if (brandBasicData?.deletedDate) {
      return brandTabs.filter((tab) => tab === 'basic');
    }
    return brandTabs;
  }, [brandBasicData]);

  const onChangeTab = (tab: BrandTabsTypes) => {
    navigate(`/brands/${id}/${tab}`);
    if (tab !== currentTab) {
      setCurrentTab(tab);
      if (tab === 'catalogues' && id) {
        mutate(id);
      }
    }
  };

  const component = useMemo(() => {
    switch (currentTab) {
      case 'basic':
        return <BrandBasicForm />;
      case 'catalogues':
        return catalogueId ? <BrandCatalogue /> : <BrandsCataloguesTable />;
      default:
        return null;
    }
  }, [currentTab, catalogueId]);

  return (
    <main className={styles.page}>
      <EntityTitle title={title} deletedDate={brandBasicData?.deletedDate} />
      <div className={styles.container}>
        <div className={styles.filters}>
          {availableTabs.map((item) => (
            <button
              key={item}
              className={`${styles.filterBtn}  ${currentTab === item && styles.filterBtnCurent}`}
              type='button'
              onClick={() => onChangeTab(item)}
              disabled={isLoading || isFetching || !id}>
              <span className={styles.btnTitle}>{t(item)}</span>
            </button>
          ))}
        </div>
        {isLoading || isFetching ? <AppLoader /> : component}
      </div>
    </main>
  );
});

export default Brand;
